import { useMemo } from 'react';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import type { TimesheetEntry, WorkingPattern } from '@/types/timesheet';
import type { OrganisationTimesheetSettings, OvertimeType } from '@/types/timesheetSettings';
import type { DayName } from '../timesheetGridUtils';

export interface UseTimesheetOvertimeParams {
  entries: TimesheetEntry[];
  visibleDays: DayName[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  employeeWorkingPattern: WorkingPattern | undefined;
}

export interface UseTimesheetOvertimeResult {
  overtimeEnabled: boolean;
  activeOvertimeTypes: OvertimeType[];
  /** ID of the org-default active work type — used to pre-populate overtime CTAs. */
  defaultOvertimeTypeId: number | undefined;
  /** Day keys (e.g. 'monday') where hours exceed the daily threshold */
  daysExceedingThreshold: Set<string>;
  weekExceedsThreshold: boolean;
}

/**
 * Overtime derived state: the active work types plus the daily/weekly threshold
 * comparisons that drive the amber "needs classifying" highlights.
 */
export function useTimesheetOvertime({
  entries,
  visibleDays,
  organizationSettings,
  employeeWorkingPattern,
}: UseTimesheetOvertimeParams): UseTimesheetOvertimeResult {
  const overtimeEnabled = organizationSettings?.overtime?.enabled === true;

  // Fetch overtime types from the OvertimeTypes API instead of organizationSettings
  const { activeTypes: apiOvertimeTypes } = useOvertimeTypes({ enabled: overtimeEnabled, screenId: 'timesheet' });

  const activeOvertimeTypes = useMemo<OvertimeType[]>(() => {
    if (!overtimeEnabled) return [];
    return apiOvertimeTypes;
  }, [overtimeEnabled, apiOvertimeTypes]);

  const defaultOvertimeTypeId = useMemo<number | undefined>(() => {
    return activeOvertimeTypes.find(t => t.isDefault)?.id;
  }, [activeOvertimeTypes]);

  /**
   * Computes daily and/or weekly threshold values.
   * Both are independently optional — only levels that are genuinely configured
   * will produce amber highlights; no phantom fallback defaults.
   */
  const overtimeThresholds = useMemo<{ daily?: number; weekly?: number } | null>(() => {
    if (!overtimeEnabled) return null;
    const thresholds = organizationSettings?.overtime?.thresholds;
    if (!thresholds || thresholds.mode === 'manual-only') return null;

    // Prefer employee working pattern over org profile
    const weeklyHours = employeeWorkingPattern?.hoursPerWeek
      || organizationSettings?.workingProfile?.standardHoursPerWeek;
    if (!weeklyHours) return null;

    // Count working days from employee pattern or org profile
    let workingDayCount: number;
    if (employeeWorkingPattern?.workingDays) {
      workingDayCount = Object.values(employeeWorkingPattern.workingDays).filter(Boolean).length;
    } else {
      const profile = organizationSettings?.workingProfile;
      workingDayCount = profile ? Object.values(profile.workingDays).filter(d => d.isWorkingDay).length : 5;
    }

    const daily = workingDayCount > 0 ? weeklyHours / workingDayCount : undefined;
    return { daily, weekly: weeklyHours };
  }, [overtimeEnabled, organizationSettings?.overtime?.thresholds, organizationSettings?.workingProfile, employeeWorkingPattern]);

  /**
   * Whether thresholdScope is 'total-hours'.
   * When true, all hours (classified + unclassified) count against the threshold.
   * When false/undefined (default 'unclassified-only'), only unclassified rows count.
   */
  const thresholdScopeTotalHours =
    organizationSettings?.overtime?.thresholds?.thresholdScope === 'total-hours';

  /**
   * Daily totals used for threshold comparison.
   * - 'unclassified-only' (default): only rows without a work type count — the highlight
   *   is a prompt to classify, not a permanent warning on already-handled rows.
   * - 'total-hours': all hours count regardless of classification.
   * Either way, only UNCLASSIFIED rows are ever highlighted.
   */
  const unclassifiedColumnTotals = useMemo(() => {
    return visibleDays.map(day =>
      entries
        .filter(entry => thresholdScopeTotalHours ? true : !entry.overtimeTypeId)
        .reduce((sum, entry) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, visibleDays, thresholdScopeTotalHours]);

  /**
   * Day-level highlighting respects the existing 'daily-overage' policy check.
   * If the admin has disabled daily-overage in Policy Checks (General tab), no day-level
   * highlights fire — regardless of what the work type threshold mode is set to.
   */
  const dailyPolicyCheckEnabled =
    organizationSettings?.policyViolations?.checks?.['daily-overage']?.enabled ?? true;

  /**
   * Week-level highlighting respects the existing 'weekly-overage' policy check.
   */
  const weeklyPolicyCheckEnabled =
    organizationSettings?.policyViolations?.checks?.['weekly-overage']?.enabled ?? true;

  const daysExceedingThreshold = useMemo<Set<string>>(() => {
    if (!dailyPolicyCheckEnabled) return new Set();
    if (!overtimeThresholds?.daily) return new Set();
    const result = new Set<string>();
    visibleDays.forEach((day, i) => {
      if (unclassifiedColumnTotals[i] > overtimeThresholds.daily!) result.add(day);
    });
    return result;
  }, [overtimeThresholds, unclassifiedColumnTotals, visibleDays, dailyPolicyCheckEnabled]);

  const unclassifiedWeekTotal = useMemo(
    () => unclassifiedColumnTotals.reduce((sum, t) => sum + t, 0),
    [unclassifiedColumnTotals]
  );
  const weekExceedsThreshold =
    weeklyPolicyCheckEnabled &&
    overtimeThresholds?.weekly != null &&
    unclassifiedWeekTotal > overtimeThresholds.weekly;

  return {
    overtimeEnabled,
    activeOvertimeTypes,
    defaultOvertimeTypeId,
    daysExceedingThreshold,
    weekExceedsThreshold,
  };
}
